/* eslint-disable eqeqeq */
import React, { useEffect, useState } from 'react';
import { Dropdown, DefaultDropdownProps, Option } from 'framework/form';
import useTechnologyService from 'domain/shared/career/technology/technologyService';
import usePositionService, { SOFTWARE_ENGINEER_ID } from '../positionService';

interface Position {
  id: number;
  name: string;
}

interface Technology {
  id: number | string;
  name: string;
}

interface PositionWithTechnologyDropdownProps extends DefaultDropdownProps {
  additionalOptions?: Option[];
  providedOptions?: Option[];
}

const createRegularPositionOptions = (positions: Position[]): Option[] => {
  return positions
    .filter(position => position.id != SOFTWARE_ENGINEER_ID)
    .map(position => ({
      label: position.name,
      value: position.id.toString(),
    }));
};

const createTechnologyPositionOptions = (
  engineerPosition: Position | undefined,
  technologies: Technology[],
): Option[] => {
  if (!engineerPosition) {
    return [];
  }

  return technologies.map(technology => ({
    label: `${technology.name} ${engineerPosition.name}`,
    value: `${engineerPosition.id.toString()}--${technology.id.toString()}`,
  }));
};

const PositionWithTechnologyDropdown: React.FC<PositionWithTechnologyDropdownProps> = ({
  additionalOptions = [],
  providedOptions,
  ...props
}) => {
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();

  const [loadedOptions, setLoadedOptions] = useState<Option[]>([]);

  useEffect(() => {
    const loadOptions = async () => {
      if (providedOptions && providedOptions.length > 0) {
        setLoadedOptions([...providedOptions, ...additionalOptions]);
        return;
      }
      const [positionsResponse, technologiesResponse] = await Promise.all([findAllPositions(), findAllTechnologies()]);
      const regularPositions = createRegularPositionOptions(positionsResponse);
      const engineerPosition = positionsResponse.findLast((position: Position) => position.id == SOFTWARE_ENGINEER_ID);
      const technologyPositions = createTechnologyPositionOptions(engineerPosition, technologiesResponse);

      setLoadedOptions([...regularPositions, ...technologyPositions, ...additionalOptions]);
    };

    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Dropdown placeholder="Select position" {...props} options={loadedOptions} />;
};

export default PositionWithTechnologyDropdown;
